import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NavigationSecurity from '../../Layouts/Navigation/NavigationSecurity';
import QrReader from 'react-qr-scanner';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faQrcode, 
    faPhone, 
    faXmark,
    faCheckCircle,
    faTimesCircle,
    faCar,
    faMotorcycle,
    faWalking,
    faParking,
    faExclamationTriangle,
    faDoorOpen
} from '@fortawesome/free-solid-svg-icons';

function VisitControl() {
    const [openDialog, setOpenDialog] = useState(false);
    const [qrResult, setQrResult] = useState('No result');
    const [visitInfo, setVisitInfo] = useState(null);
    const [scanning, setScanning] = useState(true);
    const [searchCode, setSearchCode] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [cameraError, setCameraError] = useState(false);
    const [cameraErrorMessage, setCameraErrorMessage] = useState("");

    // Recurring visitor states
    const [recurringOpen, setRecurringOpen] = useState(false);
    const [recurringInfo, setRecurringInfo] = useState(null);
    const [isRegisteringRecurring, setIsRegisteringRecurring] = useState(false);

    const [isRegisteringExit, setIsRegisteringExit] = useState(false);

    // Parking states
    const [parkingStats, setParkingStats] = useState(null);
    const [loadingParking, setLoadingParking] = useState(false);
    const [availableSpaces, setAvailableSpaces] = useState([]);
    const [selectedSpaceId, setSelectedSpaceId] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [apartmentHasParking, setApartmentHasParking] = useState(false);

    //Snack notifications
    const [openSnack, setOpenSnack] = useState(false);
    const [snackStatus, setSnackStatus] = useState("");
    const [snackMessage, setSnackMessage] = useState("");
    
    const handleClickSnack = () => {
        setOpenSnack(true);
    };

    const handleCloseSnack = () => {
        setOpenSnack(false);
    };
        useAutoDismissSnack(openSnack, setOpenSnack);

    const handleScan = (data) => {
        if (data && scanning) {
            setQrResult(data.text);
            setScanning(false);
            getInfoVisit(data.text);
        }
    };

    const handleRecurringClose = () => {
        setRecurringOpen(false);
        setRecurringInfo(null);
        setScanning(true);
    };

    const handleAdmitRecurring = () => {
        if (!recurringInfo) return;
        setIsRegisteringRecurring(true);
        axios.post(`${BASE_URL}/visitantesRecurrentes/${recurringInfo.id}/ingreso`)
            .then(() => {
                setSnackStatus('success');
                setSnackMessage('Ingreso de visitante recurrente registrado con éxito');
                handleClickSnack();
                setRecurringOpen(false);
                setRecurringInfo(null);
            })
            .catch(() => {
                setSnackStatus('error');
                setSnackMessage('Error al registrar el ingreso');
                handleClickSnack();
            })
            .finally(() => setIsRegisteringRecurring(false));
    };

    const handleError = (err) => {
        console.error("Error en cámara:", err);
        setCameraError(true);
        
        if (err.name === 'NotAllowedError') {
            setCameraErrorMessage("Permiso de cámara denegado. Por favor, permite el acceso a la cámara en la configuración de tu navegador.");
            setSnackStatus("error");
            setSnackMessage("Permiso de cámara denegado");
            handleClickSnack();
        } else if (err.name === 'NotFoundError') {
            setCameraErrorMessage("No se encontró ninguna cámara en este dispositivo.");
            setSnackStatus("error");
            setSnackMessage("No se encontró cámara");
            handleClickSnack();
        } else {
            setCameraErrorMessage("Error al acceder a la cámara: " + err.message);
            setSnackStatus("error");
            setSnackMessage("Error en la cámara");
            handleClickSnack();
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
        window.location.reload();
        setScanning(true);
    };

    // Cargar disponibilidad de parqueadero cuando se abre una visita con vehículo.
    // Si ingreso_peatonal=1 ya fue habilitado como peatonal → no hace falta verificar parqueadero.
    useEffect(() => {
        if (
            visitInfo &&
            visitInfo.visita?.tipo_vehiculo &&
            visitInfo.visita?.flag_ingreso === 0 &&
            !visitInfo.visita?.ingreso_peatonal
        ) {
            checkParkingAvailability(visitInfo.visita.tipo_vehiculo);
        }
    }, [visitInfo]);

    const checkParkingAvailability = (tipoVehiculo) => {
        setLoadingParking(true);
        setParkingStats(null);
        setAvailableSpaces([]);
        setSelectedSpaceId('');
        setApartmentHasParking(false);

        const tipo = tipoVehiculo.toUpperCase();
        const aptoId = visitInfo?.apartamento?.id_apartamento;

        Promise.all([
            axios.get(`${BASE_URL}/visitorParking/stats`),
            axios.get(`${BASE_URL}/visitorParking/spaces`)
        ])
            .then(([statsRes, spacesRes]) => {
                const stats = statsRes.data;
                const statsForType = tipo === 'CARRO' ? stats.carros : stats.motos;
                setParkingStats(statsForType);

                const allSpaces = spacesRes.data;

                // Verificar si el apartamento ya tiene un parqueadero activo
                if (aptoId) {
                    const hasActive = allSpaces.some(
                        (s) => s.id_apartamento === aptoId && s.disponible === 0
                    );
                    setApartmentHasParking(hasActive);
                }

                // Espacios disponibles para este tipo de vehículo
                const disponibles = allSpaces.filter(
                    (s) => s.tipo_vehiculo === tipo && s.disponible === 1
                );
                setAvailableSpaces(disponibles);
                if (disponibles.length > 0) setSelectedSpaceId(disponibles[0].id);
            })
            .catch((err) => {
                console.error('Error al verificar parqueadero:', err);
                setParkingStats({ total: 0, disponibles: 0, ocupados: 0 });
            })
            .finally(() => setLoadingParking(false));
    };

    const handleAssignParking = () => {
        if (!selectedSpaceId || !visitInfo) return;
        setIsRegistering(true);
        axios.post(`${BASE_URL}/visitorParking/assign`, {
            id_espacio: selectedSpaceId,
            id_visita: visitInfo.visita.id_visita,
            visitante_nombre: `${visitInfo.visita.nombre} ${visitInfo.visita.apellido}`,
            placa: visitInfo.visita.placa,
            id_usuario: visitInfo.usuario.id_usuario,
            id_apartamento: visitInfo.apartamento.id_apartamento || null,
            id_torre: visitInfo.apartamento.id_torre,
        })
            .then(() => {
                setSnackStatus('success');
                setSnackMessage('Parqueadero asignado y visita registrada con éxito');
                handleClickSnack();
                setOpenDialog(false);
            })
            .catch((err) => {
                console.error('Error al asignar parqueadero:', err);
                const msg = err.response?.data?.error || 'Error al asignar parqueadero';
                setSnackStatus('error');
                setSnackMessage(msg);
                handleClickSnack();
            })
            .finally(() => setIsRegistering(false));
    };

    // 1ra lectura: habilita entrada peatonal sin asignar parqueadero. flag_ingreso queda 0.
    const handlePedestrianEntry = () => {
        if (!visitInfo) return;
        setIsRegistering(true);
        axios.put(`${BASE_URL}/updateVisitPedestrian/${visitInfo.visita.id_visita}`)
            .then(() => {
                setSnackStatus('success');
                setSnackMessage('Entrada peatonal habilitada. El visitante debe presentar su QR en la puerta peatonal.');
                handleClickSnack();
                setOpenDialog(false);
            })
            .catch((err) => {
                console.error('Error al habilitar entrada peatonal:', err);
                setSnackStatus('error');
                setSnackMessage('Error al habilitar la entrada peatonal');
                handleClickSnack();
            })
            .finally(() => setIsRegistering(false));
    };

    // 2da lectura: confirma ingreso peatonal. Marca la visita como completada (flag_ingreso=1).
    const handleConfirmPedestrianEntry = () => {
        if (!visitInfo) return;
        setIsRegistering(true);
        axios.put(`${BASE_URL}/updateVisitFlag/${visitInfo.visita.id_visita}`, { flag_ingreso: 1 })
            .then(() => {
                setSnackStatus('success');
                setSnackMessage('Ingreso peatonal confirmado. Visita registrada con éxito.');
                handleClickSnack();
                setOpenDialog(false);
            })
            .catch((err) => {
                console.error('Error al confirmar ingreso peatonal:', err);
                setSnackStatus('error');
                setSnackMessage('Error al confirmar el ingreso peatonal');
                handleClickSnack();
            })
            .finally(() => setIsRegistering(false));
    };

    const validateToken = (token) => {
        // Validar que el token tenga exactamente 6 caracteres alfanuméricos
        const regex = /^[A-Z0-9]{6}$/;
        return regex.test(token.toUpperCase());
    };

    // Garantiza que los 4 sub-objetos existan antes de guardar en el estado
    const normalizeVisitInfo = (data) => ({
        visita: data.visita || {},
        usuario: data.usuario || {},
        apartamento: data.apartamento || {},
        torre: data.torre || {},
    });

    const handleSearchVisit = () => {
        const code = searchCode.trim().toUpperCase();

        if (!validateToken(code)) {
            setSnackStatus("error");
            setSnackMessage("El código debe tener 6 caracteres alfanuméricos (letras y números)");
            handleClickSnack();
            return;
        }

        setIsSearching(true);
        setVisitInfo(null);

        // Try normal visit first, then recurring visitor on 404
        axios.get(`${BASE_URL}/getInfoVisita/${code}`)
            .then((response) => {
                setVisitInfo(normalizeVisitInfo(response.data));
                setOpenDialog(true);
                setSearchCode('');
            })
            .catch((error) => {
                if (error.response && error.response.status === 404) {
                    // Not a normal visit — try recurring visitor
                    axios.get(`${BASE_URL}/visitanteRecurrente/qr/${code}`)
                        .then((response) => {
                            setRecurringInfo(response.data.visitante);
                            setRecurringOpen(true);
                            setSearchCode('');
                        })
                        .catch(() => {
                            setSnackStatus('error');
                            setSnackMessage('No se encontró ninguna visita ni visitante recurrente con ese código');
                            handleClickSnack();
                        })
                        .finally(() => setIsSearching(false));
                    return;
                }
                setSnackStatus("error");
                if (error.response && error.response.data && error.response.data.message) {
                    setSnackMessage(error.response.data.message);
                } else if (error.response && error.response.status === 500) {
                    setSnackMessage("Error en el servidor. Por favor, intenta nuevamente");
                } else if (error.request) {
                    setSnackMessage("No se pudo conectar con el servidor. Verifica tu conexión");
                } else {
                    setSnackMessage("Error al procesar la búsqueda");
                }
                handleClickSnack();
                setIsSearching(false);
            })
            .finally(() => {
                // Only clear searching if no inner request was made
            });
    };

    const getInfoVisit = (qrCode) => {
        console.log('QR code:', qrCode);
        // Try normal visit first, then recurring visitor on 404
        axios.get(`${BASE_URL}/getInfoVisita/${qrCode}`)
            .then((response) => {
                setVisitInfo(normalizeVisitInfo(response.data));
                setOpenDialog(true);
            })
            .catch((error) => {
                if (error.response && error.response.status === 404) {
                    axios.get(`${BASE_URL}/visitanteRecurrente/qr/${qrCode}`)
                        .then((response) => {
                            setRecurringInfo(response.data.visitante);
                            setRecurringOpen(true);
                        })
                        .catch(() => {
                            setSnackStatus('error');
                            setSnackMessage('Código no encontrado. Verifique que sea válido.');
                            handleClickSnack();
                            setScanning(true);
                        });
                } else {
                    console.error('Error getting visit information:', error);
                    setScanning(true);
                }
            });
    };

    const handleRegisterVisit = () => {
        const visitId = visitInfo.visita.id_visita; // Asegúrate de que visitInfo está disponible en el contexto adecuado
        axios.put(`${BASE_URL}/updateVisitFlag/${visitId}`, { flag_ingreso: 1 })
            .then((response) => {
                if (response.status === 200) {
                    setSnackStatus("success");
                    setSnackMessage("Visita registrada con exito!");
                    console.log('Visita registrada con éxito');
                    // Aquí puedes agregar cualquier lógica adicional que necesites después de la actualización
                } else {
                    setSnackStatus("error");
                    setSnackMessage("Error al registrar visita.");
                }
                handleClickSnack();
            })
            .catch((error) => {
                console.error('Error al actualizar la visita:', error);
                handleClickSnack();
            });
    };

    const handleRegisterExit = () => {
        if (!visitInfo) return;
        setIsRegisteringExit(true);
        axios.put(`${BASE_URL}/registerExit/${visitInfo.visita.id_visita}`)
            .then((response) => {
                const msg = response.data.parking_liberado
                    ? 'Salida registrada y parqueadero liberado con éxito'
                    : 'Salida del visitante registrada con éxito';
                setSnackStatus('success');
                setSnackMessage(msg);
                handleClickSnack();
                setOpenDialog(false);
                setScanning(true);
            })
            .catch((err) => {
                setSnackStatus('error');
                setSnackMessage(err.response?.data?.error || 'Error al registrar la salida');
                handleClickSnack();
            })
            .finally(() => setIsRegisteringExit(false));
    };

    // flag_ingreso = 1: visitante dentro del conjunto (modo salida)
    // flag_ingreso = 2: expirada/rechazada  flag_ingreso = 3: completada (visitó y salió)
    const isExitMode = !!(visitInfo && visitInfo.visita && visitInfo.visita.flag_ingreso === 1);
    const isExpiredOrCompleted = !!(visitInfo && visitInfo.visita &&
        (visitInfo.visita.flag_ingreso === 2 || visitInfo.visita.flag_ingreso === 3));
    const isVisitRegistered = isExitMode || isExpiredOrCompleted;

    const TIPO_LABELS_RV = { FAMILIAR: 'Familiar', EMPLEADO: 'Empleado', OTRO: 'Otro' };
    const TIPO_COLORS_RV = {
        FAMILIAR: 'bg-blue-100 text-blue-800',
        EMPLEADO: 'bg-purple-100 text-purple-800',
        OTRO: 'bg-orange-100 text-orange-800',
    };

    return (
        <>
            <NavigationSecurity />
            
            {/* Snackbar */}
            {openSnack && (
                <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${
                    snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
                } text-white animate-fade-in-down z-50`}>
                    <div className="flex items-center gap-2">
                        {snackStatus === 'success' ? (
                            <FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6" />
                        ) : (
                            <FontAwesomeIcon icon={faTimesCircle} className="h-6 w-6" />
                        )}
                        <span className="font-semibold">{snackMessage}</span>
                        <button onClick={handleCloseSnack} className="ml-2 hover:bg-white/20 rounded p-1">
                            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 p-4 sm:p-6 lg:p-8">
                <div className="max-w-6xl mx-auto">
                    {/* Search Section */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Búsqueda Manual</h3>
                        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <input
                                type="text"
                                placeholder="Código de visita o QR de visitante recurrente"
                                value={searchCode}
                                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && searchCode.trim().length > 0 && !isSearching) {
                                        handleSearchVisit();
                                    }
                                }}
                                className="w-full sm:w-80 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center text-lg font-semibold uppercase"
                            />
                            <button
                                onClick={handleSearchVisit}
                                disabled={searchCode.trim().length === 0 || isSearching}
                                className={`px-8 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                                    searchCode.trim().length > 0 && !isSearching
                                        ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isSearching ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        Buscando...
                                    </>
                                ) : (
                                    'Buscar visita'
                                )}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">
                            Ingresa el código de 6 caracteres de la visita o del visitante recurrente, o escanea el QR
                        </p>
                    </div>

                    {/* QR Scanner Section */}
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">
                            Control de Visitas
                        </h1>
                        
                        {cameraError ? (
                            <div className="flex flex-col items-center gap-4">
                                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 max-w-md">
                                    <FontAwesomeIcon icon={faTimesCircle} className="h-12 w-12 text-red-500 mx-auto mb-3" />
                                    <p className="text-red-800 text-center font-semibold mb-2">Error de Cámara</p>
                                    <p className="text-red-700 text-sm text-center">{cameraErrorMessage}</p>
                                    <button
                                        onClick={() => {
                                            setCameraError(false);
                                            window.location.reload();
                                        }}
                                        className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                    >
                                        Reintentar
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 text-center max-w-md">
                                    Puedes usar la búsqueda manual ingresando el código en el campo superior.
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-6">
                                <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                    Verificar visita con QR
                                </button>
                                <div className="w-full max-w-md border-4 border-teal-500 rounded-xl overflow-hidden shadow-xl">
                                    <QrReader
                                        delay={300}
                                        onError={handleError}
                                        onScan={handleScan}
                                        style={{ width: '100%' }}
                                        constraints={{ video: { facingMode: { ideal: 'environment' } } }}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 text-center">
                                    Coloca el código QR frente a la cámara
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Visit Info Dialog */}
            {openDialog && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className={`${isExitMode ? 'bg-gradient-to-r from-orange-500 to-amber-600' : 'bg-gradient-to-r from-teal-500 to-cyan-600'} px-6 py-4 flex items-center justify-between rounded-t-2xl`}>
                            <h2 className="text-2xl font-bold text-white">
                                {isExitMode ? 'Registrar Salida de Visitante' : 'Información de la Visita'}
                            </h2>
                            <button
                                onClick={handleClose}
                                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                            >
                                <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            {visitInfo && visitInfo.visita && visitInfo.visita.id_visita ? (
                                <>
                                    {/* Exit mode banner */}
                                    {isExitMode && (
                                        <div className="flex items-center gap-4 bg-orange-50 border-2 border-orange-400 rounded-xl p-4">
                                            <FontAwesomeIcon icon={faDoorOpen} className="text-orange-500 text-3xl flex-shrink-0" />
                                            <div>
                                                <p className="font-bold text-orange-800 text-lg">Visitante dentro del conjunto</p>
                                                <p className="text-sm text-orange-700 mt-1">
                                                    Este visitante ya registró su ingreso. Al confirmar, la visita quedará como completada
                                                    {visitInfo.visita.tipo_vehiculo ? ' y el parqueadero asignado será liberado.' : '.'}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Visit Information */}
                                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="bg-gray-100 px-4 py-3">
                                            <h3 className="font-bold text-gray-800 text-center">Información de la Visita</h3>
                                        </div>
                                        <div className="divide-y divide-gray-200">
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Nombre</span>
                                                <span className="text-gray-900">{visitInfo.visita.nombre} {visitInfo.visita.apellido}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Fecha</span>
                                                <span className="text-gray-900">{visitInfo.visita.fecha}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Acompañantes</span>
                                                <span className="text-gray-900">{visitInfo.visita.num_acompanantes}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Vehículo</span>
                                                <span className="text-gray-900">{visitInfo.visita.tipo_vehiculo}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Placa</span>
                                                <span className="text-gray-900">{visitInfo.visita.placa}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Observaciones</span>
                                                <span className="text-gray-900">{visitInfo.visita.observacion}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Vehicle / Parking Section */}
                                    {visitInfo.visita.tipo_vehiculo ? (
                                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                                            <div className="bg-gray-100 px-4 py-3 flex items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={visitInfo.visita.tipo_vehiculo.toUpperCase() === 'MOTO' ? faMotorcycle : faCar}
                                                    className="text-gray-600"
                                                />
                                                <h3 className="font-bold text-gray-800">Vehículo y Parqueadero</h3>
                                            </div>

                                            {isExitMode ? (
                                                /* Exit mode: visitante dentro, se liberará el parqueadero al salir */
                                                <div className="m-4 flex items-start gap-3 bg-orange-50 border border-orange-300 rounded-xl p-4">
                                                    <FontAwesomeIcon
                                                        icon={visitInfo.visita.tipo_vehiculo.toUpperCase() === 'MOTO' ? faMotorcycle : faCar}
                                                        className="text-orange-500 mt-0.5 flex-shrink-0"
                                                    />
                                                    <div>
                                                        <p className="font-semibold text-orange-800">
                                                            {visitInfo.visita.tipo_vehiculo} · Placa {visitInfo.visita.placa}
                                                        </p>
                                                        <p className="text-sm text-orange-700 mt-1">
                                                            Si hay un parqueadero asignado a esta visita, será liberado automáticamente al confirmar la salida.
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : isExpiredOrCompleted ? (
                                                /* Expirada o completada: mostrar cómo ingresó */
                                                visitInfo.visita.ingreso_peatonal >= 1 ? (
                                                    <div className="m-4 flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl p-4">
                                                        <FontAwesomeIcon icon={faWalking} className="text-amber-600 mt-0.5 flex-shrink-0" />
                                                        <div>
                                                            <p className="font-semibold text-amber-800">Ingresó por puerta peatonal</p>
                                                            <p className="text-sm text-amber-700">No había espacio de parqueadero disponible para este vehículo al momento del ingreso.</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="m-4 flex items-start gap-3 bg-green-50 border border-green-300 rounded-xl p-4">
                                                        <FontAwesomeIcon icon={faParking} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                        <div>
                                                            <p className="font-semibold text-green-800">Ingresó con parqueadero asignado</p>
                                                            <p className="text-sm text-green-700">El vehículo {visitInfo.visita.placa} ingresó y se le asignó un espacio de parqueadero.</p>
                                                        </div>
                                                    </div>
                                                )
                                            ) : (
                                                /* Not yet registered - show pedestrian confirm (2nd scan) or parking UI (1st scan) */
                                                <div className="p-4 space-y-3">
                                                    {visitInfo.visita.ingreso_peatonal === 1 ? (
                                                        /* 2nd scan: pedestrian was already enabled → just confirm entry */
                                                        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-3">
                                                            <div className="flex items-start gap-3">
                                                                <FontAwesomeIcon icon={faWalking} className="text-amber-600 mt-0.5 flex-shrink-0 text-xl" />
                                                                <div>
                                                                    <p className="font-semibold text-amber-800">Entrada peatonal habilitada</p>
                                                                    <p className="text-sm text-amber-700 mt-1">
                                                                        Este visitante fue autorizado para ingresar sin parqueadero.
                                                                        Confirma su ingreso por la puerta peatonal.
                                                                    </p>
                                                                    <div className="grid grid-cols-2 text-sm mt-2 gap-y-1">
                                                                        <span className="font-semibold text-amber-800">Tipo vehículo</span>
                                                                        <span className="text-amber-900">{visitInfo.visita.tipo_vehiculo}</span>
                                                                        <span className="font-semibold text-amber-800">Placa</span>
                                                                        <span className="text-amber-900">{visitInfo.visita.placa}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={handleConfirmPedestrianEntry}
                                                                disabled={isRegistering}
                                                                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                                                                    isRegistering
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md'
                                                                }`}
                                                            >
                                                                {isRegistering ? (
                                                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Confirmando...</>
                                                                ) : (
                                                                    <><FontAwesomeIcon icon={faWalking} /> Confirmar ingreso peatonal</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        /* 1st scan: show parking assignment UI */
                                                        <>
                                                    <div className="grid grid-cols-2 text-sm">
                                                        <span className="font-semibold text-gray-700">Tipo</span>
                                                        <span className="text-gray-900">{visitInfo.visita.tipo_vehiculo}</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 text-sm">
                                                        <span className="font-semibold text-gray-700">Placa</span>
                                                        <span className="text-gray-900">{visitInfo.visita.placa}</span>
                                                    </div>

                                                    {visitInfo.apartamento?.estado_cartera === 'en_mora' ? (
                                                        /* Bloque de mora: deshabilita parqueadero */
                                                        <div className="bg-red-50 border border-red-400 rounded-xl p-4 space-y-2">
                                                            <div className="flex items-start gap-3">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5 flex-shrink-0 text-lg" />
                                                                <div>
                                                                    <p className="font-bold text-red-800">Unidad con cartera en mora</p>
                                                                    <p className="text-sm text-red-700 mt-1">
                                                                        El apartamento <strong>{visitInfo.apartamento.num_apartamento}</strong> tiene pagos pendientes con administración.
                                                                        No se puede asignar parqueadero de visitante.
                                                                        El visitante debe ingresar únicamente por la puerta peatonal.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={handlePedestrianEntry}
                                                                disabled={isRegistering}
                                                                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                                                                    isRegistering
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md'
                                                                }`}
                                                            >
                                                                {isRegistering ? (
                                                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Registrando...</>
                                                                ) : (
                                                                    <><FontAwesomeIcon icon={faWalking} /> Habilitar entrada peatonal</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : loadingParking ? (
                                                        <div className="flex items-center gap-2 py-3 text-gray-500 text-sm">
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500"></div>
                                                            Verificando disponibilidad de parqueadero...
                                                        </div>
                                                    ) : apartmentHasParking ? (
                                                        /* Apartment already has an active parking space */
                                                        <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 space-y-3">
                                                            <div className="flex items-start gap-3">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 mt-0.5 flex-shrink-0" />
                                                                <div>
                                                                    <p className="font-semibold text-orange-800">Parqueadero ya asignado</p>
                                                                    <p className="text-sm text-orange-700 mt-0.5">
                                                                        Este apartamento ya tiene un espacio de parqueadero de visita ocupado actualmente.
                                                                        Solo se permite 1 parqueadero por apartamento a la vez.
                                                                        El visitante debe ingresar por la puerta peatonal.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={handlePedestrianEntry}
                                                                disabled={isRegistering}
                                                                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                                                                    isRegistering
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md'
                                                                }`}
                                                            >
                                                                {isRegistering ? (
                                                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Registrando...</>
                                                                ) : (
                                                                    <><FontAwesomeIcon icon={faWalking} /> Habilitar entrada peatonal</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : parkingStats && parkingStats.disponibles > 0 ? (
                                                        /* Spaces available */
                                                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                                                            <div className="flex items-center gap-2">
                                                                <FontAwesomeIcon icon={faParking} className="text-green-600" />
                                                                <span className="font-semibold text-green-800">
                                                                    {parkingStats.disponibles} espacio(s) disponible(s)
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-semibold text-gray-600 mb-1">Seleccionar espacio</label>
                                                                <select
                                                                    value={selectedSpaceId}
                                                                    onChange={(e) => setSelectedSpaceId(e.target.value)}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                                                                >
                                                                    {availableSpaces.map((s) => (
                                                                        <option key={s.id} value={s.id}>
                                                                            Espacio #{s.numero_espacio} - {s.tipo_vehiculo}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <button
                                                                onClick={handleAssignParking}
                                                                disabled={!selectedSpaceId || isRegistering}
                                                                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                                                                    !selectedSpaceId || isRegistering
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-md'
                                                                }`}
                                                            >
                                                                {isRegistering ? (
                                                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Registrando...</>
                                                                ) : (
                                                                    <><FontAwesomeIcon icon={faParking} /> Asignar parqueadero y registrar ingreso</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : parkingStats && parkingStats.disponibles === 0 ? (
                                                        /* No spaces available */
                                                        <div className="bg-red-50 border border-red-300 rounded-xl p-4 space-y-3">
                                                            <div className="flex items-start gap-3">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5 flex-shrink-0" />
                                                                <div>
                                                                    <p className="font-semibold text-red-800">Sin espacios de parqueadero disponibles</p>
                                                                    <p className="text-sm text-red-700 mt-0.5">
                                                                        No hay espacios para {visitInfo.visita.tipo_vehiculo.toLowerCase()} en este momento ({parkingStats.ocupados}/{parkingStats.total} ocupados).
                                                                        El visitante debe ingresar por la puerta peatonal.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={handlePedestrianEntry}
                                                                disabled={isRegistering}
                                                                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                                                                    isRegistering
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md'
                                                                }`}
                                                            >
                                                                {isRegistering ? (
                                                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Registrando...</>
                                                                ) : (
                                                                    <><FontAwesomeIcon icon={faWalking} /> Habilitar entrada peatonal</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : null}

                                    {/* Resident Information */}
                                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="bg-gray-100 px-4 py-3">
                                            <h3 className="font-bold text-gray-800 text-center">Residente</h3>
                                        </div>
                                        <div className="divide-y divide-gray-200">
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Nombre</span>
                                                <span className="text-gray-900">{visitInfo.usuario.nombre} {visitInfo.usuario.apellido}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Teléfono</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-900">{visitInfo.usuario.telefono}</span>
                                                    {!isVisitRegistered && (
                                                        <div className="flex gap-2">
                                                            <a
                                                                href={`tel:${visitInfo.usuario.telefono}`}
                                                                className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                                                                title="Llamar"
                                                            >
                                                                <FontAwesomeIcon icon={faPhone} className="h-5 w-5" />
                                                            </a>
                                                            <a
                                                                href={`https://wa.me/${visitInfo.usuario.telefono}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                                                                title="WhatsApp"
                                                            >
                                                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Correo</span>
                                                <span className="text-gray-900">{visitInfo.usuario.email}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Torre</span>
                                                <span className="text-gray-900">{visitInfo.torre.nombre}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Apartamento</span>
                                                <span className="text-gray-900">{visitInfo.apartamento.num_apartamento}</span>
                                            </div>
                                            <div className="grid grid-cols-2 px-4 py-3 hover:bg-gray-50">
                                                <span className="font-semibold text-gray-700">Piso</span>
                                                <span className="text-gray-900">{visitInfo.apartamento.piso}</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Cargando...</p>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                            {/* Modo salida: botón de confirmar salida (aplica a cualquier tipo de visita) */}
                            {visitInfo && visitInfo.visita && isExitMode && (
                                <button
                                    onClick={handleRegisterExit}
                                    disabled={isRegisteringExit}
                                    className={`px-8 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                                        isRegisteringExit
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                    }`}
                                >
                                    {isRegisteringExit ? (
                                        <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> Registrando salida...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faDoorOpen} /> Confirmar salida</>
                                    )}
                                </button>
                            )}
                            {/* Entrada: botón de registro para visitas sin vehículo (solo modo entrada) */}
                            {visitInfo && visitInfo.visita && !visitInfo.visita.tipo_vehiculo && !isExitMode && (
                                <button
                                    onClick={handleRegisterVisit}
                                    disabled={isExpiredOrCompleted || isRegistering}
                                    className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                                        isExpiredOrCompleted || isRegistering
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                    }`}
                                >
                                    {isExpiredOrCompleted
                                        ? (visitInfo.visita.flag_ingreso === 3 ? 'Visita completada' : 'Visita expirada')
                                        : 'Registrar visita'}
                                </button>
                            )}
                            {/* Badge de estado para visitas con vehículo expiradas o completadas */}
                            {visitInfo && visitInfo.visita && visitInfo.visita.tipo_vehiculo && isExpiredOrCompleted && (
                                <span className={`px-6 py-3 rounded-lg font-semibold ${
                                    visitInfo.visita.flag_ingreso === 3 ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                                }`}>
                                    {visitInfo.visita.flag_ingreso === 3 ? 'Visita completada' : 'Visita expirada'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Recurring Visitor Dialog */}
            {recurringOpen && recurringInfo && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                            <h2 className="text-xl font-bold text-white">Visitante Recurrente</h2>
                            <button
                                onClick={handleRecurringClose}
                                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                            >
                                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            {/* Badge */}
                            <div className="flex justify-center">
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    TIPO_COLORS_RV[recurringInfo.tipo] || 'bg-gray-100 text-gray-700'
                                }`}>
                                    {recurringInfo.tipo === 'OTRO' && recurringInfo.tipo_personalizado
                                        ? recurringInfo.tipo_personalizado
                                        : TIPO_LABELS_RV[recurringInfo.tipo] || recurringInfo.tipo}
                                </span>
                            </div>

                            {/* Visitor info */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="bg-gray-100 px-4 py-2">
                                    <h3 className="font-bold text-gray-800 text-center text-sm">Datos del Visitante</h3>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                        <span className="font-semibold text-gray-700">Nombre</span>
                                        <span className="text-gray-900">{recurringInfo.nombre} {recurringInfo.apellido}</span>
                                    </div>
                                    {recurringInfo.identificacion && (
                                        <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                            <span className="font-semibold text-gray-700">Identificación</span>
                                            <span className="text-gray-900">{recurringInfo.identificacion}</span>
                                        </div>
                                    )}
                                    {recurringInfo.telefono && (
                                        <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                            <span className="font-semibold text-gray-700">Teléfono</span>
                                            <span className="text-gray-900">{recurringInfo.telefono}</span>
                                        </div>
                                    )}
                                    {recurringInfo.email && (
                                        <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                            <span className="font-semibold text-gray-700">Email</span>
                                            <span className="text-gray-900">{recurringInfo.email}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Resident info */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="bg-gray-100 px-4 py-2">
                                    <h3 className="font-bold text-gray-800 text-center text-sm">Residente</h3>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                        <span className="font-semibold text-gray-700">Nombre</span>
                                        <span className="text-gray-900">{recurringInfo.usuario_nombre} {recurringInfo.usuario_apellido}</span>
                                    </div>
                                    <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                        <span className="font-semibold text-gray-700">Apartamento</span>
                                        <span className="text-gray-900">{recurringInfo.num_apartamento}</span>
                                    </div>
                                    <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                        <span className="font-semibold text-gray-700">Torre</span>
                                        <span className="text-gray-900">{recurringInfo.torre_nombre}</span>
                                    </div>
                                    {recurringInfo.usuario_telefono && (
                                        <div className="grid grid-cols-2 px-4 py-2.5 hover:bg-gray-50 text-sm">
                                            <span className="font-semibold text-gray-700">Teléfono</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-900">{recurringInfo.usuario_telefono}</span>
                                                <a href={`https://wa.me/${recurringInfo.usuario_telefono}`} target="_blank" rel="noopener noreferrer"
                                                    className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" title="WhatsApp">
                                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Admit button */}
                            <button
                                onClick={handleAdmitRecurring}
                                disabled={isRegisteringRecurring}
                                className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                                    isRegisteringRecurring
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                }`}
                            >
                                {isRegisteringRecurring ? (
                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Registrando ingreso...</>
                                ) : (
                                    <><FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5" /> Admitir ingreso</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default VisitControl;